// Keep in sync with DefaultRegistryProjects in backend
import {type CalloutIconProps} from '@wandb/components';

import {OLD_MODEL_REGISTRY_PROJECT_NAME} from '../../../util/artifacts';
import {AccessOption} from '../../../util/permissions';
import * as urls from '../../../util/urls';
import {type SelectionPathType} from '../../../util/urls';
import {type RegistryAccessOptionType} from './permissionUtils';
import {
  type GQLRegistryProjectType,
  type GQLRegistryProjectViewerMembershipType,
  type RegistryIcon,
} from './types';

// Registry icon defaults and helper
export const DEFAULT_REGISTRY_ICON: CalloutIconProps['icon'] =
  'book-dictionary';
export const DEFAULT_REGISTRY_COLOR: CalloutIconProps['color'] = 'moon';

export const getRegistryIconProps = (
  icon: RegistryIcon | null | undefined
) => ({
  icon: (icon?.name as CalloutIconProps['icon']) || DEFAULT_REGISTRY_ICON,
  color: (icon?.color as CalloutIconProps['color']) || DEFAULT_REGISTRY_COLOR,
});

export const REGISTRIES_OVERVIEW_TAB = 'overview';
export const REGISTRIES_ADVANCED_TAB = 'advanced';
export const REGISTRIES_LANDING_PAGE_TABS = [
  REGISTRIES_OVERVIEW_TAB,
  REGISTRIES_ADVANCED_TAB,
] as const;
export type LandingPageTab = (typeof REGISTRIES_LANDING_PAGE_TABS)[number];
export const DEFAULT_LANDING_PAGE_TAB: LandingPageTab = 'overview';

export const parseLandingTab = (t?: string): LandingPageTab =>
  t === REGISTRIES_ADVANCED_TAB
    ? REGISTRIES_ADVANCED_TAB
    : REGISTRIES_OVERVIEW_TAB;

// Keep DISALLOW_CUSTOM_REGISTRY_PROJECT_NAMES in sync with DEFAULT_REGISTRY_PROJECT_TYPES
export const DEFAULT_REGISTRY_PROJECT_TYPES = ['model', 'dataset'];

export const REGISTRY_PROJECT_PREFIX = 'wandb-registry-';

export type registryProject = {
  isRegistryProject: boolean;
  registryName?: string;
};

export const isOrgRegistryProjectName = (projectName: string | undefined) => {
  const {isRegistryProject} = checkRegistryProject(projectName);
  return isRegistryProject;
};

export const checkRegistryProject = (
  projectName: string | undefined
): registryProject => {
  if (!projectName) {
    return {isRegistryProject: false, registryName: undefined};
  }
  // Function to check if project is a registry project
  const isRegistryProject =
    projectName.startsWith(REGISTRY_PROJECT_PREFIX) &&
    projectName !== REGISTRY_PROJECT_PREFIX;
  const index = REGISTRY_PROJECT_PREFIX.length;
  const registryName = isRegistryProject
    ? projectName.substring(index)
    : undefined;
  return {isRegistryProject, registryName};
};

// For the legacy model registry, we currently fetch and display all portfolio collections
// in the model-registry project or those in other projects whose types have the substring 'model'.
// Getting the registryName right here is crucial to navigating to collections and their versions
// in the model registry.
export const getCollectionRegistryName = (
  projectName: string | undefined,
  artifactTypeName: string
): string => {
  const {registryName} = checkRegistryProject(projectName);
  if (registryName != null) {
    return registryName;
  }
  if (
    projectName === OLD_MODEL_REGISTRY_PROJECT_NAME &&
    artifactTypeName.toLowerCase().includes('model')
  ) {
    return 'model';
  }
  return '';
};

// Post the model registry migration, users should be able to see all the attributes and objects of their model registry
// collections in the context of their model-registry project. Hence, instead of just checking whether we're in the
// registry UI, we should check if the project itself is a registry project. This is relevant only for the `model-registry`
// project since for organization-level registries, we don't allow users to visit the project page.
export const showForRegistry = (
  projectName: string | undefined,
  artifactTypeName: string,
  isPortfolio: boolean,
  inRegistryContext: boolean
): boolean => {
  const isRegistryCollection =
    getCollectionRegistryName(projectName, artifactTypeName) !== '' &&
    isPortfolio;
  return inRegistryContext || isRegistryCollection;
};

type User = {
  id: string;
  name: string;
  photoUrl?: string | null;
};

export type globalRegistryType = {
  id: string;
  owner?: User;
  entityName: string;
  orgId: string;
  orgName: string;
  registryName: string;
  registryProjectName: string;
  registryProjectID: string;
  description: string;
  lastActive?: string | null;
  updatedAt?: string | null;
  createdAt?: string;
  types: string[] | null;
  collectionCount: number;
  access: RegistryAccessOptionType;
  allowAllArtifactTypesInRegistry: boolean;
  searchTerm?: string;
  protectedAliases: string[];
  registryProjectInternalId: string;
  icon: RegistryIcon | null;
};

/**
 * Temporary util till we deprecate Model registry
 * to handle entity-scoped urls
 */
export function resolveRegistryUrl(
  isOrgRegistry: boolean,
  orgName: string,
  registryName?: string,
  selectionPath?: SelectionPathType,
  entityName?: string,
  selectedCollectionView?: string,
  selectedMembershipIdentifier?: string,
  selectedTab?: string
) {
  if (isOrgRegistry || registryName !== 'model') {
    return urls.globalRegistry(
      orgName,
      registryName,
      selectionPath,
      selectedCollectionView,
      selectedMembershipIdentifier,
      selectedTab
    );
  }
  return urls.objectRegistry(
    registryName,
    selectionPath,
    entityName,
    selectedCollectionView,
    selectedMembershipIdentifier,
    selectedTab
  );
}

// Get the GlobalRegistryType struct from a registry project
export function getGlobalRegistryType(
  registryProject: GQLRegistryProjectType,
  regName: string,
  entityName: string,
  orgId: string,
  orgName: string
): globalRegistryType {
  const typeNodes = registryProject.artifactTypes?.edges;
  const types: string[] = [];
  typeNodes?.forEach(type => {
    if (type.node?.name != null) {
      types.push(type.node?.name);
    }
  });
  const collectionCount = registryProject.totalArtifactCollections;
  const registryOwner =
    registryProject.user == null ? undefined : registryProject.user;

  return {
    id: registryProject.id,
    owner: registryOwner,
    entityName,
    orgId,
    orgName,
    registryName: regName,
    registryProjectName: registryProject.name,
    description: registryProject.description ?? '',
    lastActive: registryProject.lastActive,
    updatedAt: registryProject.updatedAt ?? registryProject.createdAt, // to make sorting work with _.orderBy(), otherwise the nulls will be at the beginning
    createdAt: registryProject.createdAt,
    types,
    collectionCount,
    access:
      registryProject.access === 'RESTRICTED'
        ? AccessOption.Restricted
        : AccessOption.Private,
    allowAllArtifactTypesInRegistry:
      registryProject.allowAllArtifactTypesInRegistry,
    registryProjectID: registryProject.id,
    protectedAliases: registryProject.protectedArtifactAliases.map(
      alias => alias.alias
    ),
    registryProjectInternalId: registryProject.internalId,
    icon: registryProject.icon
      ? {
          name: registryProject.icon.name,
          color: registryProject.icon.color,
        }
      : null,
  };
}

export const isViewerRegistryMemberFromQuery = (
  viewerMembership: GQLRegistryProjectViewerMembershipType
): boolean => {
  const memberID = viewerMembership.member?.id;
  const teamMemberIDs = (viewerMembership.teamMembers ?? []).flatMap(team =>
    team.team.member?.id ? [team.team.member.id] : []
  );

  return memberID != null || teamMemberIDs.length > 0;
};
